const SENSITIVE_QUERY_PARAMS = [
  'token',
  't',
  'jwt',
  'code',
  'reset_token',
  'access_token',
  'id_token',
  'refresh_token',
  'state',
  'signature',
  'sig',
] as const;

/**
 * Worker portal paths are `/{companySlug}/{workerSlug}/…`, and a worker slug is
 * built from the worker's own name. The portal is a worker-audience surface, so
 * the name must not reach an analytics property.
 *
 * The all-in-one dev bundle serves the same routes under a `/worker` prefix.
 */
const WORKER_BASE_PATH_SEGMENT = 'worker';

/** `StringUtils.slugify` appends `-` + a 7-character nanoid. */
const SLUG_SUFFIX_LENGTH = 7;

const REDACTED_SLUG = '[worker]';

const URL_PROPERTIES = [
  '$current_url',
  '$referrer',
  '$session_entry_url',
  '$session_entry_referrer',
  '$initial_current_url',
  '$initial_referrer',
] as const;

const PATHNAME_PROPERTIES = [
  '$pathname',
  '$session_entry_pathname',
  '$initial_pathname',
] as const;

/** PostHog nests the `$initial_*` properties under these. */
const NESTED_PROPERTY_BAGS = ['$set', '$set_once'] as const;

type SanitizableEvent =
  | {
      properties?: Record<string, unknown>;
    }
  | null
  | undefined;

/**
 * Matches the output of `StringUtils.slugify({ suffix: true })`: a base with no
 * dash (slugify turns every dash into an underscore), then a dash, then the
 * 7-character suffix. A UUID, a camelCase route name and a dashed static
 * segment all fail one of the three conditions.
 */
export function isSuffixedSlug(segment: string): boolean {
  const dashIndex = segment.length - SLUG_SUFFIX_LENGTH - 1;

  if (dashIndex < 1 || segment[dashIndex] !== '-') return false;

  return (
    /^[a-z0-9_]+$/.test(segment.slice(0, dashIndex)) &&
    /^[a-z0-9_-]+$/.test(segment.slice(dashIndex + 1))
  );
}

export function redactWorkerSlug(pathname: string): string {
  const segments = pathname.split('/');
  // segments[0] is the empty string before the leading slash.
  const workerIndex = segments[1] === WORKER_BASE_PATH_SEGMENT ? 3 : 2;
  const segment = segments[workerIndex];

  if (!segment || !isSuffixedSlug(segment)) return pathname;

  segments[workerIndex] = REDACTED_SLUG;

  return segments.join('/');
}

export function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    let changed = false;

    for (const param of SENSITIVE_QUERY_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[redacted]');
        changed = true;
      }
    }

    const pathname = redactWorkerSlug(parsed.pathname);
    if (pathname !== parsed.pathname) changed = true;

    if (!changed) return url;

    // The placeholder is assembled by hand: assigning it to `parsed.pathname`
    // percent-escapes the brackets.
    const absolute = `${parsed.origin}${pathname}${parsed.search}${parsed.hash}`;

    return url.startsWith(parsed.origin)
      ? absolute
      : absolute.slice(parsed.origin.length);
  } catch {
    return url;
  }
}

function sanitizeProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> | null {
  const next = { ...properties };
  let changed = false;

  for (const key of URL_PROPERTIES) {
    const value = next[key];
    if (typeof value !== 'string') continue;

    const sanitized = sanitizeUrl(value);
    if (sanitized !== value) {
      next[key] = sanitized;
      changed = true;
    }
  }

  for (const key of PATHNAME_PROPERTIES) {
    const value = next[key];
    if (typeof value !== 'string') continue;

    const sanitized = redactWorkerSlug(value);
    if (sanitized !== value) {
      next[key] = sanitized;
      changed = true;
    }
  }

  for (const key of NESTED_PROPERTY_BAGS) {
    const bag = next[key];
    if (!bag || typeof bag !== 'object' || Array.isArray(bag)) continue;

    const sanitized = sanitizeProperties(bag as Record<string, unknown>);
    if (sanitized) {
      next[key] = sanitized;
      changed = true;
    }
  }

  return changed ? next : null;
}

export function sanitizePostHogEvent<T extends SanitizableEvent>(event: T): T {
  if (!event?.properties) {
    return event;
  }

  const properties = sanitizeProperties(event.properties);

  return properties ? { ...event, properties } : event;
}
